import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumberFormatValues } from 'react-number-format';
import { useNavigate } from 'react-router-dom';

import { LinkButton, StandardButton } from '@eika/button';
import { CurrencyInput, formatCurrencyValue } from '@eika/currency-input';
import { Input } from '@eika/input';
import { Loader } from '@eika/loader';
import { ButtonToggles } from '@eika/radio-button';

import { AppContext, DEFAULT_MARKEDSRENTE, DEFAULT_NEDBETALINGSTID } from '../containers/kalkulatorState';
import { useConfig } from '../default-config';
import { piwikTrackId, piwikTrackName } from '../gtm/gtmUtils';
import { useMarkedsrente } from '../hooks/hooks';
import Nedbetalingsplan from './nedbetalingsplan';
import { calculateAnnuitetsbeloep } from './nedbetalingsplanList';
import PrecentInput from './precentInput';
import Slider from './slider';

import pengesekk from '../resources/pengesekk.svg';

const Laanekostnad: React.FC = () => {
    const { t } = useTranslation();
    const { links } = useConfig();
    const navigate = useNavigate();
    const { data: markedsrente, isLoading } = useMarkedsrente();
    const { laaneparam, setLaaneparam } = useContext(AppContext);
    const scollToNedbetalingsplan = useRef<HTMLDivElement>(null);
    const [kostnad, setKostnad] = useState<number>(0);
    const [valgtNedbetalingsperiode, setValgtNedbetalingsperiode] = useState<string>(String(DEFAULT_NEDBETALINGSTID));
    const [nedbetalingstidRequired, setNedbetalingstidRequired] = useState<string>('');
    const [nedbetalingstidError, setNedbetalingstidError] = useState<string>('');
    const [showNedbetalingsplan, setShowNedbetalingsplan] = useState<boolean>(false);

    useEffect(() => {
        if (laaneparam.laanebeloep > 10000 && Number(laaneparam.rente) > 0 && laaneparam.nedbetalingstid > 0) {
            setKostnad(
                calculateAnnuitetsbeloep(
                    laaneparam.laanebeloep,
                    laaneparam.rente,
                    laaneparam.nedbetalingstid,
                    markedsrente?.terminGebyr || 0,
                ),
            );
        } else {
            setKostnad(0);
            setShowNedbetalingsplan(false);
        }
    }, [laaneparam, markedsrente]);

    useEffect(() => {
        if (scollToNedbetalingsplan.current && showNedbetalingsplan) {
            scollToNedbetalingsplan.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [showNedbetalingsplan]);

    const onChangeRente = (values: NumberFormatValues) => {
        if (Number(values.value) > 10) {
            setLaaneparam({ ...laaneparam, rente: 10 });
        } else {
            setLaaneparam({ ...laaneparam, rente: Number(values.value) });
        }
    };

    const onChangeNedbetalingstid = (periode: string) => {
        setValgtNedbetalingsperiode(periode);
        setLaaneparam({ ...laaneparam, nedbetalingstid: periode === 'ANNEN' ? 0 : Number(periode) });
    };

    const onChangeManuellNedbetalingstid = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (Number(event.target.value) > 30) {
            setLaaneparam({ ...laaneparam, nedbetalingstid: Number('30') });
            setNedbetalingstidError(t('laanekostnad.nedbetalingstidError'));
        } else {
            setLaaneparam({ ...laaneparam, nedbetalingstid: Number(event.target.value) });
            setNedbetalingstidRequired('');
            setNedbetalingstidError('');
        }
    };

    const onClickTilbakestillRente = () =>
        setLaaneparam({ ...laaneparam, rente: markedsrente?.markedsrente || DEFAULT_MARKEDSRENTE });

    const onClickShowNedbetalingsplan = () => {
        if (Number(laaneparam.laanebeloep) > 10000 && laaneparam.rente > 0 && laaneparam.nedbetalingstid > 0) {
            setShowNedbetalingsplan(!showNedbetalingsplan);
        } else {
            setShowNedbetalingsplan(false);
            setNedbetalingstidRequired(t('laanekostnad.nedbetalingstidRequired'));
        }
    };

    const onClickSoekBoliglaan = () => {
        window.location.replace(links.boliglaanurl.url);
    };

    const onClickLinkBUttonHvorMyeKanJegLaane = () => {
        navigate('/');
    };

    if (isLoading)
        return (
            <div className="margin-top-xtra-large">
                <Loader />
            </div>
        );

    return (
        <div className="laanekostand">
            <div className="sgw-layout">
                <div className="sgw-layout-item one">
                    <div>
                        <h2 id="boliglaankalkulator" className="margin-bottom-medium-large">
                            {t('laaneramme.boliglaanKalkulator')}
                        </h2>
                        <div className="margin-bottom-larger">
                            <LinkButton
                                className="font-size-small margin-right-medium-large"
                                onClick={onClickLinkBUttonHvorMyeKanJegLaane}
                                data-track-id={piwikTrackId('linker')}
                                data-track-linkname={piwikTrackName('hvor-mye-kan-jeg-laane')}
                            >
                                {t('laaneramme.hvorMyeKanJegLaane')}
                            </LinkButton>
                            <span className="font-size-small bold">{t('laaneramme.hvaKosterLaanet')}</span>
                        </div>
                        <CurrencyInput
                            name="laanebeloep"
                            placeholder="0 kr"
                            className="max-width-200 margin-bottom-medium"
                            label={t('laanekostnad.oensketLaanebeloep')}
                            value={laaneparam.laanebeloep}
                            onChange={(e: { type: string; target: { name: string; value: string | number } }) =>
                                setLaaneparam({
                                    ...laaneparam,
                                    laanebeloep: Number(e.target.value),
                                })
                            }
                        />
                        <PrecentInput
                            name="rente"
                            maxLength={laaneparam.rente >= 10 ? 3 : 6}
                            label={t('laanekostnad.rentePaaLaanet')}
                            sublabel={t('laanekostnad.rentePaaLaanetSubLabel')}
                            buttonlabel={t('laanekostnad.renteTilbakestill')}
                            className="margin-bottom-small"
                            value={laaneparam.rente || undefined}
                            onValueChange={onChangeRente}
                            onClickButton={onClickTilbakestillRente}
                            dataTrackId={piwikTrackId('laanekostnad-rente-paa-laanet')}
                            dataTrackLinkname={piwikTrackName('laanekostnad-rente-tilbakestill')}
                        />
                        <Slider
                            min={0}
                            max={10}
                            step={0.01}
                            rente={laaneparam.rente}
                            onChangeRente={(rente) => setLaaneparam({ ...laaneparam, rente })}
                        />
                        <div className="margin-bottom-medium-large ">
                            <ButtonToggles
                                label={t('laanekostnad.nedbetalingstid')}
                                dataTrackId={piwikTrackId(t('laanekostnad.nedbetalingstid'))}
                                className="max-width-desktop-80-precent"
                                selectedOption={valgtNedbetalingsperiode}
                                onChange={onChangeNedbetalingstid}
                                options={[
                                    { label: `15 ${t('laanekostnad.aar')}`, value: '15' },
                                    { label: `20 ${t('laanekostnad.aar')}`, value: '20' },
                                    { label: `25 ${t('laanekostnad.aar')}`, value: '25' },
                                    { label: `30 ${t('laanekostnad.aar')}`, value: '30' },
                                    { label: t('laanekostnad.annen'), value: 'ANNEN' },
                                ]}
                            />
                        </div>
                        {valgtNedbetalingsperiode === 'ANNEN' && (
                            <Input
                                placeholder="Antall år"
                                className="max-width-200"
                                maxLength={2}
                                onChange={onChangeManuellNedbetalingstid}
                                label={t('laanekostnad.oensketNedbetalingstid')}
                                error={nedbetalingstidRequired || nedbetalingstidError}
                            />
                        )}
                    </div>
                </div>
                <div className="sgw-layout-item two">
                    <div className="row align-items">
                        <img src={pengesekk} alt="pengesekk" />
                        <div className="margin-bottom-large">
                            <div className="max-300">
                                <span className="typography-ingress">{t('laanekostnad.maanedligKostnad')}</span>
                                <div>
                                    <span className="number-extra-large">{formatCurrencyValue(kostnad)}</span> kr
                                </div>
                            </div>
                        </div>
                        <div className="margin-bottom-large">
                            <StandardButton
                                onClick={onClickSoekBoliglaan}
                                data-track-id={piwikTrackId('linker')}
                                data-track-linkname={piwikTrackName('soek-boliglaan')}
                            >
                                {t('laaneramme.soekBoliglaan')}
                            </StandardButton>
                        </div>
                        <LinkButton
                            onClick={onClickShowNedbetalingsplan}
                            data-track-id={
                                showNedbetalingsplan
                                    ? piwikTrackId('nedbetalingsplan')
                                    : piwikTrackId('nedbetalingsplan')
                            }
                            data-track-linkname={
                                showNedbetalingsplan
                                    ? piwikTrackName(t('nedbetalingsplan.lukkNedbetalingsplan'))
                                    : piwikTrackName(t('nedbetalingsplan.visNedbetalingsplan'))
                            }
                        >
                            {showNedbetalingsplan
                                ? t('nedbetalingsplan.lukkNedbetalingsplan')
                                : t('nedbetalingsplan.visNedbetalingsplan')}
                        </LinkButton>
                    </div>
                </div>
                {showNedbetalingsplan && (
                    <div className="sgw-layout-item three" ref={scollToNedbetalingsplan}>
                        <Nedbetalingsplan
                            laanebeloep={Number(laaneparam.laanebeloep)}
                            rente={laaneparam.rente}
                            nedbetalingstid={laaneparam.nedbetalingstid}
                            showNedbetalingsplan={showNedbetalingsplan}
                            setShowNedbetalingsplan={setShowNedbetalingsplan}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Laanekostnad;
